import { ComponentProps, FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Icon, MaterialSymbol, Modal } from 'vopay';
import { Button, PaymentMethodLogo, DropdownMenu } from 'UI';
import usePaymentMethods, { PaymentMethod, PaymentMethodType } from '@/hooks/usePaymentMethods';

import { usePermissions } from '@/providers/Permissions.provider';
import { useTranslation } from 'react-i18next';
import ProvidePaymentMethod from '@/components/Customer/ProvidePaymentMethod/ProvidePaymentMethod';
import Options, { type OptionPaymentMethod } from '@/components/Customer/ProvidePaymentMethod/Options';
import type { FormValues as AddInteracFormValues } from '@/components/Customer/AddInteracModal';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useDataFetchIndicator } from '@/hooks';
import { createPortal } from 'react-dom';

type PaymentMethodsDropdownProps = {
  accountName: string;
  accountID: string;
  email: string;
  disabled: boolean;
  triggerSize?: 'small' | 'standard';
};

// These are NOT translatable strings!!! They are keys for single collection/single payment's `type` prop, lol
const typeMap: Record<PaymentMethodType, string> = {
  BankAccount: 'Bank Account',
  CreditCard: 'Credit Card',
  DebitCard: 'Debit Card',
  Email: 'Interac e-Transfer',
  PhoneNumber: 'Interac e-Transfer',
  GooglePay: 'Google Pay',
  ApplePay: 'Apple Pay',
  PayPal: 'PayPal',
  Venmo: 'Venmo',
};

// TODO: there is a lot of crap code in this file (ie using 'type' to govern behavior; calling payment-methods for EVERY item in the table), only made to accommodate the old single payments page.
// The single payments page is going to be rewritten, so this file and parent component should be almost completely redone at that time.
const PaymentMethodsDropdown: FC<PaymentMethodsDropdownProps> = ({
  accountName,
  accountID,
  triggerSize = 'standard',
  disabled,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Contacts' });
  const { t: tGlobal } = useTranslation('translation', { keyPrefix: 'Global' });
  const { permissions } = usePermissions();
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const [, setPending] = useDataFetchIndicator();

  const { fetching, methods, fetchMethods, setDefaultMethod } = usePaymentMethods({
    accountID,
  });

  useEffect(() => {
    if (!disabled) {
      fetchMethods();
    }
  }, [disabled]);

  const collectMethods = useMemo(() => {
    return methods.filter((m) => m.type !== 'DebitCard');
  }, [methods]);

  const paymentMethods = useMemo(() => {
    const interacTypes: PaymentMethodType[] = ['Email', 'PhoneNumber'];

    return methods.filter((m) => {
      if (interacTypes.includes(m.type)) return m.hasQuestionAndAnswer;

      return m.type !== 'CreditCard' && m.type !== 'DebitCard';
    });
  }, [methods]);

  function handleSelectExisting(pathname: string, state?: object) {
    history.push({
      pathname,
      state,
    });
  }

  async function handleAddInterac(
    eTransferMethod: AddInteracFormValues['eTransferMethod'],
    token: string,
    pathname: string,
  ) {
    const setDefaultResponse = await setDefaultMethod(eTransferMethod, token);

    if (!setDefaultResponse.Success) {
      console.error(setDefaultResponse.ErrorMessage);

      await alertDialog.open(
        t('Dialog.AddMethod.assignDefault', {
          defaultValue: 'Payment method has been successfully added. Please assign a default payment method later',
        }),
        null,
        {
          abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
        },
      );
    }
    history.push(pathname, {
      token: token,
      contact: accountID,
    });
  }

  async function handleCreditCardAdded(token: string, pathname: string) {
    const res = await setDefaultMethod('CreditCard', token);

    if (res.ErrorMessage) {
      await alertDialog.open(
        t('Dialog.AddMethod.assignDefault', {
          defaultValue: 'Payment method has been successfully added. Please assign a default payment method later.',
        }),
        null,
        {
          abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
        },
      );
    }
    history.push(pathname, {
      token: token,
      contact: accountID,
    });
  }

  async function handleDebitCardAdded(token: string, pathname: string) {
    const res = await setDefaultMethod('DebitCard', token);

    if (res.ErrorMessage) {
      await alertDialog.open(
        t('Dialog.AddMethod.assignDefault', {
          defaultValue: 'Payment method has been successfully added. Please assign a default payment method later.',
        }),
        null,
        {
          abortLabel: tGlobal('Buttons.close', { defaultValue: 'Close' }),
        },
      );
    }

    history.push(pathname, {
      token: token,
      contact: accountID,
    });
  }

  async function handleBankAccountAdded(token: string, pathname: string) {
    history.push(pathname, {
      token: token,
      contact: accountID,
    });
  }

  async function handleAdd(type: PaymentMethodType, token: string, pathname: string) {
    try {
      setPending(true);

      if (type === 'BankAccount') {
        await handleBankAccountAdded(token, pathname);
      } else if (type === 'DebitCard') {
        await handleDebitCardAdded(token, pathname);
      } else if (type === 'CreditCard') {
        await handleCreditCardAdded(token, pathname);
      } else if (type === 'Email' || type === 'PhoneNumber') {
        await handleAddInterac(type, token, pathname);
      }
    } finally {
      setPending(false);
    }
  }

  function handleRequest(type: OptionPaymentMethod, pathname: string) {
    history.push({
      pathname,
      state: {
        contact: 'request',
        type: typeMap[type],
      },
    });
  }

  return (
    <>
      {permissions.CreateCollections && (
        <Selector
          size={triggerSize}
          disabled={fetching || disabled}
          methods={collectMethods}
          accountName={accountName}
          accountID={accountID}
          onSelect={(state) => handleSelectExisting('/single-collection', state)}
          onProvide={(type, token) => handleAdd(type, token, '/single-collection')}
          onRequest={(type) => handleRequest(type, '/single-collection')}
        >
          <div
            style={{
              position: 'relative',
            }}
          >
            {fetching && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon icon="loader" size={triggerSize === 'standard' ? 'xlg' : 'md'} />
              </div>
            )}

            <div
              style={{
                opacity: fetching ? 0 : 1,
              }}
            >
              {t('collection', {
                defaultValue: 'Collection',
              })}
            </div>
          </div>
        </Selector>
      )}

      {permissions.CreatePayments && (
        <Selector
          size={triggerSize}
          disabled={fetching || disabled}
          methods={paymentMethods}
          omit={['CreditCard']}
          accountName={accountName}
          accountID={accountID}
          onSelect={(state) => handleSelectExisting('/single-payment', state)}
          onProvide={(type, token) => handleAdd(type, token, '/single-payment')}
          onRequest={(type) => handleRequest(type, '/single-payment')}
        >
          {fetching && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon icon="loader" size={triggerSize === 'standard' ? 'xlg' : 'md'} />
            </div>
          )}

          <div
            style={{
              opacity: fetching ? 0 : 1,
            }}
          >
            {t('payment', {
              defaultValue: 'Payment',
            })}
          </div>
        </Selector>
      )}
    </>
  );
};

function Selector({
  children,
  size = 'standard',
  disabled,
  methods,
  accountName,
  accountID,
  omit = [],
  onSelect = () => {},
  onProvide = () => {},
  onRequest = () => {},
}: {
  children: ReactNode;
  size: 'small' | 'standard';
  disabled: boolean;
  methods: PaymentMethod[];
  accountName: string;
  accountID: string;
  omit?: ComponentProps<typeof ProvidePaymentMethod>['omit'];
  onSelect: (state: object) => void;
  onProvide: (type: PaymentMethodType, token: string) => void;
  onRequest: (type: OptionPaymentMethod) => void;
}) {
  const { t } = useTranslation('translations', { keyPrefix: 'Contacts.PaymentMethodMenu' });
  const [showingProvide, setShowingProvide] = useState(false);
  const [showingRequest, setShowingRequest] = useState(false);

  function handleOnAdd(method: PaymentMethodType, token: string) {
    setShowingProvide(false);
    onProvide(method, token);
  }

  function handleOnRequest(type: OptionPaymentMethod) {
    setShowingRequest(false);
    onRequest(type);
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <Button
            size={size}
            disabled={disabled}
            style={{
              position: 'relative',
            }}
          >
            {children}
          </Button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content align="end">
          <DropdownMenu.Item left={<MaterialSymbol icon="add" weight="900" />} onSelect={() => setShowingProvide(true)}>
            <b>{t('providePaymentMethod', { defaultValue: 'Provide New Payment Method' })}</b>
          </DropdownMenu.Item>

          {/* TODO: enable once we have the updated Single Payment/Collection screens */}
          {/* <DropdownMenu.Item
            right={<MaterialSymbol icon="add" weight="900" />}
            onSelect={() => setShowingRequest(true)}
          >
            <b>{t('requestPaymentMethod', { defaultValue: 'Request New Payment Method' })}</b>
          </DropdownMenu.Item> */}

          {methods.map((method) => (
            <Item key={method.token} accountID={accountID} method={method} onSelect={onSelect} />
          ))}
        </DropdownMenu.Content>
      </DropdownMenu>

      <ProvidePaymentMethod
        isOpen={showingProvide}
        accountName={accountName}
        accountID={accountID}
        omit={omit}
        onClose={() => setShowingProvide(false)}
        onAddInterac={(token, eTransferMethod) => {
          handleOnAdd(eTransferMethod, token);
        }}
        onBankAdded={(token) => handleOnAdd('BankAccount', token)}
        onCreditCardAdded={(token) => handleOnAdd('CreditCard', token)}
        onDebitCardAdded={(token) => handleOnAdd('DebitCard', token)}
      />

      {createPortal(
        <Modal isVisible={showingRequest} enableBgClick={false}>
          <Options omit={omit} onClose={() => setShowingRequest(false)} onSelect={handleOnRequest} />
        </Modal>,
        document.getElementById('modal-root')!,
      )}
    </>
  );
}

export default PaymentMethodsDropdown;

type ItemProps = {
  accountID: string;

  method: PaymentMethod;
  onSelect: (state: object) => void;
};
function Item({ accountID, method, onSelect }: ItemProps) {
  async function handleSelect() {
    onSelect({
      token: method.token,
      contact: accountID,
    });
  }

  return (
    <DropdownMenu.Item
      key={method.token}
      onSelect={handleSelect}
      description={method.details}
      left={
        <PaymentMethodLogo
          method={method.type}
          bankId={method.bankId}
          creditCardBrand={method.creditCardBrand}
          size="24px"
        />
      }
    >
      {method.accountHolderName}
    </DropdownMenu.Item>
  );
}
